import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { CanalOrcamento } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import CampoBusca from '../componentes/CampoBusca';
import Ordenacao from '../componentes/Ordenacao';
import { listarOrcamentos } from '../lib/consultas';
import { brl, dataHoraBR, hojeISO } from '../lib/formato';
import { casaBusca, porData, porNumero, porTexto } from '../lib/listas';
import {
  CANAIS_ORCAMENTO, dataCurtaDeISO, rotuloDoCanal, situacaoOrcamento,
  type SituacaoOrcamento,
} from '../lib/orcamento';
import { mensagemDeErro } from '../lib/supabase';

// Tela 16 — orçamentos emitidos. Todo perfil ativo vê TODOS (diferente de
// vendas): o cliente volta outro dia e outro vendedor precisa achar o papel
// pelo código. A taxa de conversão nos cartões diz se orçamento aqui é
// ferramenta de venda ou só papel gasto.

const COR_BADGE: Record<SituacaoOrcamento, string> = {
  aberto: 'border-ok/60 text-ok',
  convertido: 'border-edge text-dim',
  vencido: 'border-alerta/60 text-alerta',
};

type OrdemOrcamento = 'recente' | 'antigo' | 'maior' | 'menor' | 'cliente' | 'vence';

const ORDENS_ORCAMENTO = [
  { id: 'recente', rotulo: 'Mais recente' },
  { id: 'antigo', rotulo: 'Mais antigo' },
  { id: 'maior', rotulo: 'Maior valor' },
  { id: 'menor', rotulo: 'Menor valor' },
  { id: 'cliente', rotulo: 'Cliente (A–Z)' },
  { id: 'vence', rotulo: 'Vence antes' },
] as const satisfies ReadonlyArray<{ id: OrdemOrcamento; rotulo: string }>;

export default function Orcamentos() {
  // Orçamento dura mais que venda: a janela padrão é 30 dias, não 7.
  const [de, setDe] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return hojeISO(d);
  });
  const [ate, setAte] = useState(hojeISO());
  const [somenteAbertos, setSomenteAbertos] = useState(false);
  // 049 — o filtro que responde "quanto do que se cota por telefone vira
  // venda?". Sem ele o canal seria só enfeite na linha.
  const [canal, setCanal] = useState<CanalOrcamento | 'todos'>('todos');
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<OrdemOrcamento>('recente');

  const periodo = useMemo(() => {
    const inicio = new Date(`${de}T00:00:00`);
    const fim = new Date(`${ate}T00:00:00`);
    fim.setDate(fim.getDate() + 1);
    return { de: inicio.toISOString(), ate: fim.toISOString() };
  }, [de, ate]);

  const orcamentos = useQuery({
    queryKey: ['orcamentos', periodo.de, periodo.ate],
    queryFn: () => listarOrcamentos({ de: periodo.de, ate: periodo.ate }),
  });

  const hoje = hojeISO();
  // O canal e a busca recortam ANTES de tudo: os cartões passam a ser a
  // conversão DAQUELE recorte, que é a pergunta do dono ("o que se cota por
  // telefone fecha?", "quanto desse cliente virou venda?").
  //
  // "SÓ OS ABERTOS" FICA DE FORA DESTA BASE, e isso é deliberado: ele esconde
  // justamente os convertidos, então a taxa de conversão cairia para 0% toda
  // vez que alguém marcasse a caixa. Ele recorta só a LISTA, nunca os cartões.
  const doCanal = useMemo(
    () => (orcamentos.data ?? []).filter((o) => (
      (canal === 'todos' || o.canal === canal)
      // E AS PEÇAS (11/09/2026), pelo mesmo motivo das vendas e das encomendas.
      && casaBusca(busca, o.codigo, o.cliente_nome, o.vendedor, o.veiculo, o.observacao,
                   o.quote_items.map((i) => `${i.descricao} ${i.sku ?? ''} ${i.codigo_avulso ?? ''}`).join(' '))
    )),
    [orcamentos.data, canal, busca],
  );
  const linhas = useMemo(() => {
    const todas = doCanal.map((o) => ({
      ...o,
      situacao: situacaoOrcamento(o, hoje),
    }));
    const visiveis = somenteAbertos ? todas.filter((o) => o.situacao === 'aberto') : todas;
    return [...visiveis].sort((a, b) => {
      if (ordem === 'antigo') return porData(a.created_at, b.created_at, 'asc');
      if (ordem === 'maior') return porNumero(Number(a.total), Number(b.total), 'desc');
      if (ordem === 'menor') return porNumero(Number(a.total), Number(b.total), 'asc');
      if (ordem === 'cliente') {
        return porTexto(a.cliente_nome, b.cliente_nome) || porData(a.created_at, b.created_at);
      }
      // "Vence antes" é a ordem de LIGAR para o cliente: o papel que perde a
      // validade primeiro está no topo, e o já vencido não interessa mais.
      if (ordem === 'vence') return porData(a.valido_ate, b.valido_ate, 'asc');
      return porData(a.created_at, b.created_at);
    });
  }, [doCanal, somenteAbertos, hoje, ordem]);

  const filtrando = busca.trim() !== '' || canal !== 'todos' || somenteAbertos;

  const totais = useMemo(() => {
    const todas = doCanal;
    const convertidos = todas.filter((o) => o.convertido_em).length;
    return {
      emitidos: todas.length,
      abertos: todas.filter((o) => situacaoOrcamento(o, hoje) === 'aberto').length,
      convertidos,
      taxa: todas.length ? Math.round((convertidos / todas.length) * 100) : 0,
    };
  }, [doCanal, hoje]);

  return (
    <Layout titulo="Orçamentos">
      <div className="space-y-4">
        <section className="cartao space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="rotulo" htmlFor="de">De</label>
              <input id="de" type="date" className="campo" value={de}
                     onChange={(e) => setDe(e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor="ate">Até</label>
              <input id="ate" type="date" className="campo" value={ate}
                     onChange={(e) => setAte(e.target.value)} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {([['todos', 'Todos'], ...CANAIS_ORCAMENTO.map((c) => [c.id, c.rotulo] as const)] as const)
              .map(([id, rotulo]) => (
                <button key={id} type="button" onClick={() => setCanal(id as CanalOrcamento | 'todos')}
                        aria-pressed={canal === id}
                        className={`border px-3 py-1 text-xs font-bold ${
                          canal === id ? 'border-signal bg-signal/10' : 'border-edge text-mute'}`}>
                  {rotulo}
                </button>
              ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-mute">
            <input type="checkbox" className="h-4 w-4" checked={somenteAbertos}
                   onChange={(e) => setSomenteAbertos(e.target.checked)} />
            Só os abertos (na validade, ainda sem venda)
          </label>

          <CampoBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder="Buscar por código, cliente, vendedor ou veículo"
          />

          <Ordenacao opcoes={ORDENS_ORCAMENTO} valor={ordem} aoMudar={setOrdem} />
        </section>

        <section className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="cartao p-3 sm:p-4">
            <p className="rotulo">Emitidos</p>
            <p className="text-base font-black tabular-nums sm:text-2xl">{totais.emitidos}</p>
          </div>
          <div className="cartao p-3 sm:p-4">
            <p className="rotulo">Abertos</p>
            <p className="text-base font-black tabular-nums sm:text-2xl">{totais.abertos}</p>
          </div>
          <div className="cartao p-3 sm:p-4">
            <p className="rotulo">Viraram venda</p>
            <p className="text-base font-black tabular-nums sm:text-2xl">
              {totais.convertidos}
              <span className="ml-1 text-xs font-semibold text-dim">({totais.taxa}%)</span>
            </p>
          </div>
        </section>

        {orcamentos.error ? <Aviso tipo="erro">{mensagemDeErro(orcamentos.error)}</Aviso> : null}
        {orcamentos.isLoading && <Carregando texto="Carregando orçamentos" />}

        <section className="cartao p-4">
          <h2 className="rotulo">
            Documentos ({filtrando
              ? `${linhas.length} de ${orcamentos.data?.length ?? 0}`
              : linhas.length})
          </h2>
          {linhas.length > 0 ? (
            <ul className="divide-y divide-edge">
              {linhas.map((o) => (
                <li key={o.id}>
                  <Link to={`/orcamentos/${o.id}`} className="flex items-start justify-between gap-3 py-3 hover:bg-panel-alt">
                    <div className="min-w-0">
                      <p className="font-semibold">
                        <span className="font-mono text-signal-soft">{o.codigo}</span>
                        {o.cliente_nome && <span className="font-normal text-mute"> · {o.cliente_nome}</span>}
                      </p>
                      <p className="text-xs text-dim">
                        {rotuloDoCanal(o.canal)}
                        {' · '}{dataHoraBR(o.created_at)}
                        {o.vendedor && ` · ${o.vendedor}`}
                        {' · válido até '}{dataCurtaDeISO(o.valido_ate)}
                      </p>
                      {o.veiculo && <p className="truncate text-xs text-mute">{o.veiculo}</p>}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-bold tabular-nums">{brl(Number(o.total))}</p>
                      <span className={`border px-1.5 py-0.5 font-mono text-[0.625rem] uppercase ${COR_BADGE[o.situacao]}`}>
                        {o.situacao === 'convertido' ? 'virou venda' : o.situacao}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            !orcamentos.isLoading && (
              <p className="mt-3 text-sm text-dim">
                {filtrando && (orcamentos.data?.length ?? 0) > 0
                  ? 'Nenhum orçamento do período casa com esses filtros. Limpe a busca ou volte o canal para “Todos”.'
                  : 'Nenhum orçamento no período. Emita pela tela de Venda — monte o carrinho e toque em “Salvar como orçamento”.'}
              </p>
            )
          )}
        </section>
      </div>
    </Layout>
  );
}
